import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiRequest } from "../common/api/apiClient.js";
import type {
  AvatarUploadTicketDTO,
  MeDTO,
  UpdateSettingsRequest,
  UserPresence,
} from "../common/api/types.js";
import { meQueryKey } from "../tenant/query/meQueries.js";

/**
 * Einstellungen der Oberfläche speichern. Die Anzeige wechselt sofort; scheitert das
 * Speichern, holt React Query den echten Stand zurück.
 */
export function useUpdateSettings() {
  const client = useQueryClient();
  return useMutation<MeDTO, Error, UpdateSettingsRequest>({
    mutationFn: (body) => apiRequest<MeDTO>("/me/settings", { method: "PUT", body }),
    onMutate: async (body) => {
      await client.cancelQueries({ queryKey: meQueryKey });
      const previous = client.getQueryData<MeDTO>(meQueryKey);
      if (previous) {
        client.setQueryData<MeDTO>(meQueryKey, {
          ...previous,
          settings: { ...previous.settings, ...body },
        });
      }
      return { previous } as never;
    },
    onError: (_error, _body, context) => {
      const previous = (context as { previous?: MeDTO } | undefined)?.previous;
      if (previous) {
        client.setQueryData(meQueryKey, previous);
      }
    },
    onSuccess: (me) => {
      client.setQueryData(meQueryKey, me);
    },
  });
}

export function useSetPresence() {
  const client = useQueryClient();
  return useMutation<MeDTO, Error, UserPresence>({
    mutationFn: (presence) =>
      apiRequest<MeDTO>("/me/presence", { method: "PUT", body: { presence } }),
    onSuccess: (me) => {
      client.setQueryData(meQueryKey, me);
      // Das Verzeichnis zeigt den Status mit an.
      void client.invalidateQueries({ queryKey: ["members"] });
    },
  });
}

/**
 * Profilbild setzen: Ticket holen, Bild direkt in den Objektspeicher schicken,
 * bestätigen. Derselbe Weg wie bei Dateien — der Inhalt läuft nie durch die API.
 */
export function useSetAvatar() {
  const client = useQueryClient();
  return useMutation<MeDTO, Error, File>({
    mutationFn: async (file) => {
      const ticket = await apiRequest<AvatarUploadTicketDTO>("/me/avatar", {
        method: "POST",
        body: { mimeType: file.type, sizeBytes: file.size },
      });

      const response = await fetch(ticket.uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!response.ok) {
        throw new Error("Das Bild konnte nicht in den Speicher geschrieben werden.");
      }

      // Erst die Bestätigung schaltet auf das neue Bild um; scheitert der Upload,
      // bleibt das bisherige stehen.
      return apiRequest<MeDTO>("/me/avatar/confirm", {
        method: "POST",
        body: { storageKey: ticket.storageKey },
      });
    },
    onSuccess: (me) => {
      client.setQueryData(meQueryKey, me);
      void client.invalidateQueries({ queryKey: ["members"] });
    },
  });
}

export function useRemoveAvatar() {
  const client = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: () => apiRequest<void>("/me/avatar", { method: "DELETE" }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: meQueryKey });
      void client.invalidateQueries({ queryKey: ["members"] });
    },
  });
}
